'use client'

import React from 'react'
import { usePathname } from 'next/navigation'
import type { Locale } from '@/i18n'
import { SingleCTA } from './variants/SingleCTA'
import { DualCTA } from './variants/DualCTA'

export type CTAVariant = 'single' | 'dual' | 'none'

interface StickyCTAProps {
  locale: Locale
  variant?: CTAVariant
  className?: string
}

// Pages where sticky CTA should NOT appear
const EXCLUDED_ROUTES: string[] = []

// Route-specific variant configuration
const ROUTE_VARIANTS: Record<string, CTAVariant> = {
  '/statistics': 'dual',
  '/statistics/color-schemes': 'none',
}

export function StickyCTA({ locale, variant, className }: StickyCTAProps) {
  const pathname = usePathname()

  // Remove locale prefix from pathname for matching
  const pathWithoutLocale = pathname.replace(/^\/(en|uk)/, '') || '/'

  // Check if current route is excluded
  const isExcluded = EXCLUDED_ROUTES.some((route) => {
    if (route === '/') return pathWithoutLocale === '/'
    return pathWithoutLocale.startsWith(route)
  })

  if (isExcluded) {
    return null
  }

  // Determine variant: explicit prop > route config > default
  let effectiveVariant: CTAVariant = variant || 'single'

  if (!variant) {
    // Check route-specific configuration
    for (const [route, routeVariant] of Object.entries(ROUTE_VARIANTS)) {
      if (route === '/' && pathWithoutLocale === '/') {
        effectiveVariant = routeVariant
        break
      } else if (route !== '/' && pathWithoutLocale.startsWith(route)) {
        effectiveVariant = routeVariant
        break
      }
    }
  }

  // Render appropriate variant
  switch (effectiveVariant) {
    case 'single':
      return <SingleCTA locale={locale} className={className} />
    case 'dual':
      return <DualCTA locale={locale} className={className} />
    case 'none':
      return null
    default:
      return null
  }
}
